import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  Users, ArrowLeft, Edit2, Check, X, TrendingUp, 
  Link2, Copy, ExternalLink
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function StatusBadge({ status }) {
  const map = {
    active: { label: 'Actif', cls: 'bg-green-100 text-green-800' },
    pending: { label: 'En attente', cls: 'bg-amber-100 text-amber-800' },
    inactive: { label: 'Inactif', cls: 'bg-gray-100 text-gray-600' },
  };
  const s = map[status] || map.inactive;
  return <span className={`text-xs px-2 py-1 rounded font-medium ${s.cls}`}>{s.label}</span>;
}

function EditableRow({ affiliate, onSave, siteOrigin }) {
  const [editing, setEditing] = useState(false);
  const [commission, setCommission] = useState(affiliate.commission_rate);
  const [status, setStatus] = useState(affiliate.status);
  const [slug, setSlug] = useState(affiliate.slug);

  const handleSave = () => {
    onSave(affiliate.id, { commission_rate: parseFloat(commission), status, slug });
    setEditing(false);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(`${siteOrigin}/r/${affiliate.slug}`);
    toast.success('Lien copié !');
  };

  return (
    <Card className="p-4 mb-3" data-testid={`affiliate-row-${affiliate.id}`}>
      <div className="space-y-3">
        {/* Top row: Info + Edit button */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <p className="font-semibold text-sm text-foreground truncate">
                {affiliate.prenom} {affiliate.nom}
              </p>
              <StatusBadge status={affiliate.status} />
              <Badge variant="outline" className="text-xs font-semibold">
                {affiliate.commission_rate}%
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground truncate">{affiliate.email}</p>
            {affiliate.entreprise && (
              <p className="text-xs text-muted-foreground">{affiliate.entreprise}</p>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {editing ? (
              <>
                <Button size="sm" variant="ghost" onClick={handleSave} className="h-7 w-7 p-0"
                  data-testid={`affiliate-save-${affiliate.id}`}>
                  <Check className="w-4 h-4 text-green-600" />
                </Button>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                  onClick={() => { setEditing(false); setCommission(affiliate.commission_rate); setStatus(affiliate.status); setSlug(affiliate.slug); }}>
                  <X className="w-4 h-4 text-destructive" />
                </Button>
              </>
            ) : (
              <Button size="sm" variant="ghost" onClick={() => setEditing(true)} className="h-7 w-7 p-0"
                data-testid={`affiliate-edit-${affiliate.id}`}>
                <Edit2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Link row */}
        <div className="flex items-center gap-1.5">
          {editing ? (
            <div className="flex items-center gap-1 flex-1 min-w-0">
              <span className="text-xs text-muted-foreground flex-shrink-0">/r/</span>
              <Input value={slug} onChange={(e) => setSlug(e.target.value)}
                className="h-7 text-xs flex-1" data-testid={`affiliate-slug-edit-${affiliate.id}`} />
            </div>
          ) : (
            <button onClick={copyLink}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors min-w-0"
              title="Copier le lien" data-testid={`affiliate-copy-link-${affiliate.id}`}>
              <Link2 className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate">/r/{affiliate.slug}</span>
              <Copy className="w-3 h-3 flex-shrink-0" />
            </button>
          )}
        </div>

        {/* Stats row */}
        <div className="flex items-center gap-4 sm:gap-6 text-xs">
          <div>
            <span className="font-semibold text-foreground">{affiliate.total_referrals}</span>
            <span className="text-muted-foreground ml-1">clics</span>
          </div>
          <div>
            <span className="font-semibold text-foreground">{affiliate.total_conversions}</span>
            <span className="text-muted-foreground ml-1">ventes</span>
          </div>
          <div>
            <span className="font-semibold text-accent">CHF {(affiliate.total_commission_earned || 0).toFixed(2)}</span>
            <span className="text-muted-foreground ml-1">commissions</span>
          </div>
        </div>

        {/* Edit fields */}
        {editing && (
          <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-border">
            <div className="flex items-center gap-1">
              <label className="text-xs text-muted-foreground">Commission:</label>
              <Input type="number" value={commission} onChange={(e) => setCommission(e.target.value)}
                className="h-7 text-xs w-16" min="0" max="100" step="0.5"
                data-testid={`affiliate-commission-edit-${affiliate.id}`} />
              <span className="text-xs text-muted-foreground">%</span>
            </div>
            <div className="flex items-center gap-1">
              <label className="text-xs text-muted-foreground">Statut:</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)}
                className="h-7 text-xs border border-border rounded px-2 bg-card"
                data-testid={`affiliate-status-edit-${affiliate.id}`}>
                <option value="pending">En attente</option>
                <option value="active">Actif</option>
                <option value="inactive">Inactif</option>
              </select>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

export default function AdminAffiliates() {
  const navigate = useNavigate();
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  const siteOrigin = window.location.origin;

  const token = localStorage.getItem('admin_token');

  const fetchAffiliates = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/affiliates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAffiliates(res.data);
    } catch (err) {
      if (err.response?.status === 401) navigate('/admin/login');
      toast.error('Erreur chargement affiliés');
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!token) { navigate('/admin/login'); return; }
    fetchAffiliates();
  }, [token, navigate, fetchAffiliates]);

  const handleSave = async (id, data) => {
    try {
      await axios.put(`${API_URL}/api/admin/affiliates/${id}`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Affilié mis à jour');
      fetchAffiliates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur mise à jour');
    }
  };

  const filtered = affiliates.filter(a => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (
      a.nom?.toLowerCase().includes(q) ||
      a.prenom?.toLowerCase().includes(q) ||
      a.email?.toLowerCase().includes(q) ||
      a.slug?.toLowerCase().includes(q)
    );
  });

  // Stats summary
  const totalActive = affiliates.filter(a => a.status === 'active').length;
  const totalPending = affiliates.filter(a => a.status === 'pending').length;
  const totalCommissions = affiliates.reduce((s, a) => s + (a.total_commission_earned || 0), 0);
  const totalConversions = affiliates.reduce((s, a) => s + (a.total_conversions || 0), 0);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b border-border px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Dashboard</span>
          </button>
          <Separator orientation="vertical" className="h-5" />
          <h1 className="text-base sm:text-xl font-bold text-foreground truncate" data-testid="admin-affiliates-title">
            Gestion des affiliés
          </h1>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <Card className="p-3 sm:p-4 text-center">
            <p className="text-xl sm:text-2xl font-bold text-primary">{affiliates.length}</p>
            <p className="text-xs text-muted-foreground">Total</p>
          </Card>
          <Card className="p-3 sm:p-4 text-center">
            <p className="text-xl sm:text-2xl font-bold text-green-700">{totalActive}</p>
            <p className="text-xs text-muted-foreground">Actifs</p>
          </Card>
          <Card className="p-3 sm:p-4 text-center">
            <p className="text-xl sm:text-2xl font-bold text-foreground">{totalConversions}</p>
            <p className="text-xs text-muted-foreground">Conversions</p>
          </Card>
          <Card className="p-3 sm:p-4 text-center">
            <p className="text-lg sm:text-2xl font-bold text-accent">CHF {totalCommissions.toFixed(0)}</p>
            <p className="text-xs text-muted-foreground">Commissions</p>
          </Card>
        </div>

        {/* Pending alert */}
        {totalPending > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 sm:mb-6 flex items-center gap-2">
            <Badge className="bg-amber-500 text-white">{totalPending}</Badge>
            <p className="text-xs sm:text-sm text-amber-900">
              demande{totalPending > 1 ? 's' : ''} en attente
            </p>
          </div>
        )}

        {/* Search */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
          <Input
            placeholder="Rechercher..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="sm:max-w-xs"
            data-testid="affiliate-search-input"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              navigator.clipboard.writeText(`${siteOrigin}/devenir-partenaire`);
              toast.success('Lien d\'inscription copié');
            }}
            className="text-xs sm:text-sm"
            data-testid="copy-registration-link"
          >
            <ExternalLink className="w-4 h-4 mr-1.5" />
            Copier lien inscription
          </Button>
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Chargement...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">Aucun affilié trouvé</p>
          </div>
        ) : (
          filtered.map(a => (
            <EditableRow
              key={a.id}
              affiliate={a}
              onSave={handleSave}
              siteOrigin={siteOrigin}
            />
          ))
        )}
      </div>
    </div>
  );
}
